/**
 * TermsAndConditions.jsx — Terms & Conditions page
 *
 * Accessible at /terms. Required for Twilio A2P 10DLC registration.
 * Covers SMS program details, opt-out instructions, and general
 * booking terms for Viri's Clayworks.
 */
import { Link } from 'react-router-dom';

const EFFECTIVE_DATE = 'April 15, 2026';
const CONTACT_EMAIL = 'hello@virisclayworks.com';
const SITE_URL = 'virisclayworks.com';

export default function TermsAndConditions() {
  return (
    <div
      className="fade"
      style={{ padding: '32px 48px 64px', maxWidth: 860, margin: '0 auto' }}
    >
      {/* Header */}
      <div
        className="tile"
        style={{ padding: '40px 48px 36px', marginBottom: 24 }}
      >
        <p style={s.eyebrow}>Viri&apos;s Clayworks</p>
        <h1 style={s.heading}>Terms &amp; Conditions</h1>
        <p style={s.sub}>Effective date: {EFFECTIVE_DATE}</p>
      </div>

      {/* Body */}
      <div className="tile" style={{ padding: '40px 48px', lineHeight: 1.8 }}>
        <p style={s.body}>
          Welcome to Viri&apos;s Clayworks. By booking a class or using our
          website, you agree to these Terms &amp; Conditions. Please read them
          carefully.
        </p>

        <Section title="1. Bookings">
          <ul style={s.list}>
            <li>
              All class bookings are subject to availability and confirmed only
              after successful payment.
            </li>
            <li>
              You will receive an email confirmation with your booking details
              after checkout.
            </li>
            <li>
              We reserve the right to cancel or reschedule a session due to low
              enrollment, instructor availability, or circumstances beyond our
              control. We will notify you promptly and offer a full refund or
              reschedule option.
            </li>
          </ul>
        </Section>

        <Section title="2. Cancellations & Refunds">
          <ul style={s.list}>
            <li>
              Cancellations made at least 48 hours before the class start time
              may be eligible for a refund or credit at our discretion.
            </li>
            <li>
              Cancellations made less than 48 hours before the class are
              generally non-refundable.
            </li>
            <li>
              Refunds are returned to the original payment method and may take
              5–10 business days to appear.
            </li>
          </ul>
        </Section>

        <Section title="3. SMS Messaging Program">
          <p style={s.body}>
            <strong>Program name:</strong> Viri&apos;s Clayworks Notifications
          </p>
          <p style={s.body}>
            <strong>Program description:</strong> Transactional SMS messages
            related to your pottery class bookings, including booking
            confirmations, 24-hour reminders, booking updates, cancellations,
            and pickup notifications when your pottery pieces are ready for
            collection.
          </p>
          <p style={s.body}>
            <strong>Message frequency:</strong> Varies based on your booking
            activity. You may receive up to 5 messages per booking
            (confirmation, reminder, and any updates or notifications).
          </p>
          <p style={s.body}>
            <strong>Message &amp; data rates may apply.</strong> Check with your
            mobile carrier for details.
          </p>
          <p style={s.body}>
            <strong>Opt-out:</strong> Reply <strong>STOP</strong> at any time to
            unsubscribe from SMS notifications. You will receive a one-time
            confirmation message and no further messages will be sent.
          </p>
          <p style={s.body}>
            <strong>Help:</strong> Reply <strong>HELP</strong> for assistance,
            or contact us at{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} style={s.link}>
              {CONTACT_EMAIL}
            </a>
            .
          </p>
          <p style={s.body}>
            <strong>Supported carriers:</strong> All major US carriers. Carrier
            is not liable for delayed or undelivered messages.
          </p>
        </Section>

        <Section title="4. Email Newsletter">
          <p style={s.body}>
            You can subscribe to our email newsletter on our website to receive
            updates about new classes, events, and studio news.
          </p>
          <ul style={s.list}>
            <li>
              <strong>Opt-in:</strong> You subscribe by entering your email
              address in the newsletter sign-up form on our website. Submission
              constitutes your consent to receive marketing emails from
              Viri&apos;s Clayworks.
            </li>
            <li>
              <strong>Opt-out:</strong> You can unsubscribe at any time by
              clicking the <strong>Unsubscribe</strong> link at the bottom of
              any newsletter email. You will be removed from our mailing list
              promptly.
            </li>
            <li>
              <strong>Frequency:</strong> We send newsletters occasionally —
              typically when new classes or events are announced. We do not send
              daily emails.
            </li>
            <li>
              <strong>Content:</strong> Newsletters may include class
              announcements, studio news, promotions, and pottery tips. We do
              not share your email address with third parties for marketing
              purposes.
            </li>
          </ul>
          <p style={s.body}>
            For questions about our newsletter, contact us at{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} style={s.link}>
              {CONTACT_EMAIL}
            </a>
            .
          </p>
        </Section>

        <Section title="5. Studio Conduct">
          <ul style={s.list}>
            <li>
              Please arrive on time. Late arrivals may not be accommodated and
              are not eligible for a refund.
            </li>
            <li>
              All skill levels are welcome. Our classes are designed to be
              beginner-friendly.
            </li>
            <li>
              All materials, instructions, firing, and glazing are included in
              the class price unless otherwise stated.
            </li>
            <li>
              Participants must follow all studio safety guidelines provided by
              the instructor.
            </li>
          </ul>
        </Section>

        <Section title="6. Pottery Pickup">
          <ul style={s.list}>
            <li>
              After your class, your pottery will go through firing and glazing,
              which typically takes several weeks.
            </li>
            <li>
              You will be notified by email and/or SMS when your pieces are
              ready for pickup.
            </li>
            <li>
              Unclaimed pottery after 60 days may be donated or discarded at our
              discretion.
            </li>
          </ul>
        </Section>

        <Section title="7. Intellectual Property">
          <p style={s.body}>
            All content on {SITE_URL} — including images, text, and design — is
            owned by Viri&apos;s Clayworks and may not be reproduced without
            written permission.
          </p>
        </Section>

        <Section title="8. Limitation of Liability">
          <p style={s.body}>
            Viri&apos;s Clayworks is not liable for any injuries, damages, or
            losses occurring during classes beyond those caused by our own gross
            negligence. Participation in pottery classes involves inherent risks
            that participants accept upon booking.
          </p>
        </Section>

        <Section title="9. Governing Law">
          <p style={s.body}>
            These Terms are governed by the laws of the State of Texas. Any
            disputes will be resolved in the courts of Texas.
          </p>
        </Section>

        <Section title="10. Changes to These Terms">
          <p style={s.body}>
            We may update these Terms at any time. Continued use of our website
            or services after changes constitutes acceptance of the updated
            Terms.
          </p>
        </Section>

        <Section title="11. Contact Us">
          <p style={s.body}>Questions about these Terms? Reach us at:</p>
          <p style={{ ...s.body, marginTop: 8 }}>
            <strong>Viri&apos;s Clayworks</strong>
            <br />
            <a href={`mailto:${CONTACT_EMAIL}`} style={s.link}>
              {CONTACT_EMAIL}
            </a>
            <br />
            {SITE_URL}
          </p>
        </Section>

        <div
          style={{
            borderTop: '1px solid #E8E0CC',
            marginTop: 40,
            paddingTop: 24,
          }}
        >
          <Link to="/privacy" style={s.link}>
            Privacy Policy →
          </Link>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginTop: 32 }}>
      <h2 style={s.sectionTitle}>{title}</h2>
      {children}
    </div>
  );
}

const s = {
  eyebrow: {
    fontFamily: "'Raleway', sans-serif",
    fontWeight: 700,
    fontSize: '0.65rem',
    letterSpacing: '0.2em',
    textTransform: 'uppercase',
    color: '#9C7A3C',
    margin: '0 0 10px',
  },
  heading: {
    fontFamily: 'Georgia, serif',
    fontWeight: 400,
    fontSize: '2rem',
    color: '#1a1208',
    margin: '0 0 8px',
  },
  sub: {
    fontFamily: "'Raleway', sans-serif",
    fontSize: '0.82rem',
    color: '#9C7A3C',
    margin: 0,
  },
  sectionTitle: {
    fontFamily: 'Georgia, serif',
    fontWeight: 400,
    fontSize: '1.1rem',
    color: '#1a1208',
    margin: '0 0 10px',
  },
  body: {
    fontFamily: "'Raleway', sans-serif",
    fontSize: '0.92rem',
    color: '#3a2e1e',
    margin: '0 0 12px',
  },
  list: {
    fontFamily: "'Raleway', sans-serif",
    fontSize: '0.92rem',
    color: '#3a2e1e',
    margin: '0 0 12px',
    paddingLeft: 24,
    lineHeight: 1.9,
  },
  link: {
    color: '#9C7A3C',
    textDecoration: 'underline',
  },
};
